import { useCallback, useEffect, useRef, useState } from 'react';

export { renderFailureMessage } from './pdfFailure';
import type { RenderFailure } from './pdfFailure';
import type { RenderedPage, RenderResponse } from './pdfRender.worker';

/** Driving the page rasteriser from React. One worker per component, created on
 *  the first request rather than on mount: the renderer is a large download and
 *  most samples are not PDFs. */

export type PagesState =
  | { status: 'idle' }
  | { status: 'rendering' }
  | { status: 'done'; pages: RenderedPage[]; totalPages: number }
  | { status: 'failed'; reason: RenderFailure };

/** How many pages to rasterise. The first pages carry the lure, and a page
 *  count is attacker-chosen. */
const MAX_PAGES = 10;

export function usePdfPages() {
  const workerRef = useRef<Worker | null>(null);
  const nextID = useRef(0);
  /** The request still wanted, so a slow render cannot overwrite newer pages. */
  const wantedID = useRef(-1);
  const [state, setState] = useState<PagesState>({ status: 'idle' });

  /** Bitmaps hold memory collection does not reclaim promptly, so each set is
   *  closed when replaced or unmounted. */
  const liveBitmaps = useRef<RenderedPage[]>([]);
  const releaseBitmaps = useCallback(() => {
    for (const page of liveBitmaps.current) page.bitmap.close();
    liveBitmaps.current = [];
  }, []);

  useEffect(() => {
    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
      releaseBitmaps();
    };
  }, [releaseBitmaps]);

  const render = useCallback(
    (bytes: Uint8Array, width: number) => {
      if (!workerRef.current) {
        workerRef.current = new Worker(new URL('./pdfRender.worker.ts', import.meta.url), {
          type: 'module',
        });
        workerRef.current.onmessage = (event: MessageEvent<RenderResponse>) => {
          const result = event.data;
          if (result.id !== wantedID.current) {
            // A superseded render still allocated bitmaps.
            if (result.ok) for (const p of result.pages) p.bitmap.close();
            return;
          }
          releaseBitmaps();
          if (result.ok) {
            liveBitmaps.current = result.pages;
            setState({ status: 'done', pages: result.pages, totalPages: result.totalPages });
          } else {
            setState({ status: 'failed', reason: result.reason });
          }
        };
        workerRef.current.onerror = () => setState({ status: 'failed', reason: 'unavailable' });
      }

      const id = nextID.current++;
      wantedID.current = id;
      setState({ status: 'rendering' });
      workerRef.current.postMessage({ id, bytes, maxPages: MAX_PAGES, width }, [bytes.buffer]);
    },
    [releaseBitmaps]
  );


  return { state, render };
}
